/**
 * QuestionFitCard.jsx
 *
 * Reliability companion card. Assesses how strongly the simulation question is
 * connected to the demographics available in the uploaded population.
 *
 * Renders a High / Moderate / Low fit badge, plain-English explanation,
 * key demographics used, and any signals the model thinks are missing.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, AlertCircle, Target } from 'lucide-react';
import api from '../../lib/api';
import { cn } from '../../lib/utils';

const FIT_CONFIG = {
  high: {
    label: 'High',
    Icon: CheckCircle2,
    badgeCls: 'bg-positive-soft text-positive-soft-foreground border-positive-border',
    iconCls: 'text-positive-soft-foreground',
  },
  moderate: {
    label: 'Moderate',
    Icon: AlertTriangle,
    badgeCls: 'bg-warning-soft text-warning-soft-foreground border-warning-border',
    iconCls: 'text-warning-soft-foreground',
  },
  low: {
    label: 'Low',
    Icon: AlertCircle,
    badgeCls: 'bg-negative-soft text-negative-soft-foreground border-negative-border',
    iconCls: 'text-negative-soft-foreground',
  },
};

export default function QuestionFitCard({ simulationId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFit = async (refresh = false) => {
    if (!simulationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        `/simulations/${simulationId}/question-fit${refresh ? '?refresh=true' : ''}`,
        { _silent: true },
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load question fit analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationId]);

  const fit = (data?.fit || 'moderate').toLowerCase();
  const cfg = FIT_CONFIG[fit] || FIT_CONFIG.moderate;
  const Icon = cfg.Icon;

  return (
    <Card className="border border-slate-200 dark:border-border" data-testid="question-fit-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Target className="w-4 h-4 text-slate-400" />
            Question Fit Analysis
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800"
            onClick={() => fetchFit(true)}
            disabled={loading}
            data-testid="question-fit-refresh-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span className="ml-1">Re-analyze</span>
          </Button>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          How structurally connected this question is to the demographics available in your population.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="question-fit-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Analyzing question fit...
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 p-2.5 bg-negative-soft border border-negative-border rounded text-xs text-negative-soft-foreground" data-testid="question-fit-error">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {data && (
          <>
            <div className="flex items-center gap-2 flex-wrap" data-testid="question-fit-result">
              <Badge variant="outline" className={cn('px-2 py-1 text-xs font-semibold gap-1.5', cfg.badgeCls)} data-testid="question-fit-badge">
                <Icon className={cn('w-3.5 h-3.5', cfg.iconCls)} />
                Question Fit: {cfg.label}
              </Badge>
              {data.source === 'heuristic' && (
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">
                  Heuristic (LLM unavailable)
                </Badge>
              )}
            </div>

            <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed" data-testid="question-fit-explanation">
              {data.explanation}
            </p>

            {Array.isArray(data.key_demographics) && data.key_demographics.length > 0 && (
              <div data-testid="question-fit-key-demographics">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  Most relevant demographics
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {data.key_demographics.map((d) => (
                    <Badge key={d} variant="outline" className="text-[11px] bg-slate-50 text-slate-700 border-slate-200">
                      {d}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(data.missing_signals) && data.missing_signals.length > 0 && (
              <div data-testid="question-fit-missing-signals">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  Likely missing signals
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {data.missing_signals.map((d) => (
                    <Badge key={d} variant="outline" className="text-[11px] bg-warning-soft text-warning-soft-foreground border-warning-border">
                      {d}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
